import React, { useEffect, useMemo, useState } from "react";
import { auth, provider, isConfigValid, onAuthStateChanged, signInWithPopup, signOut, User } from "../firebase";

const allowedEmails = Array.isArray(__ALLOWED_EMAILS__) ? __ALLOWED_EMAILS__ : [];

export function AuthGate({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const isAllowed = useMemo(() => {
    if (!user) return false;
    if (allowedEmails.length === 0) return true;
    const email = (user.email || "").toLowerCase();
    return allowedEmails.map((e) => e.toLowerCase()).includes(email);
  }, [user]);

  useEffect(() => {
    if (!user || !auth) return;
    if (!isAllowed) {
      void signOut(auth);
    }
  }, [user, isAllowed]);

  async function handleLogin() {
    if (!auth || !provider) return;
    try {
      setError(null);
      await signInWithPopup(auth, provider);
    } catch (e: any) {
      setError(e?.message || "Login failed");
    }
  }

  async function handleLogout() {
    if (!auth) return;
    await signOut(auth);
  }

  if (!isConfigValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-surface border border-border rounded-lg p-6">
          <h1 className="text-lg font-semibold">Site Locked</h1>
          <p className="text-sm text-muted mt-2">
            Firebase Auth is not configured. Add your Firebase web config to the build env vars
            and redeploy.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted">
        Loading...
      </div>
    );
  }

  if (!user || !isAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-surface border border-border rounded-lg p-6 space-y-3">
          <h1 className="text-lg font-semibold">Sign in required</h1>
          <p className="text-sm text-muted">
            This site is private. Sign in with an allowed Google account to continue.
          </p>
          {error && <div className="text-xs text-red-400">{error}</div>}
          <button
            onClick={handleLogin}
            className="px-3 py-2 rounded-md bg-[#4FC3F7] text-[#0C0F14] text-sm font-semibold hover:bg-[#81D4FA] transition"
          >
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
